import { toast } from "sonner";
import { Megaphone } from "lucide-react";
import type { Agent } from "@/types";
import { SectionCard } from "@/components/SectionCard";
import { useUpdateRelayMode } from "@/api/mutations/relayMode";

// #244: Outbound-Relay-Policy. Multi-Select (unabhängige Flags), KEINE exklusiven Modi.
// Beide aus (Default) = heutiges Verhalten: nur explizite mesh__send-Calls, finale
// Textantwort verworfen. final = finale Antwort zusätzlich weiterleiten (modell-agnostische
// Garantie, z.B. für Nicht-Claude-Modelle). verbose = zusätzlich Zwischenschritte
// (Tool-Namen); verbose ⊇ final. mesh__send (Attachments) bleibt immer verfügbar.
export function RelaySection({ agent }: { agent: Agent }) {
  const update = useUpdateRelayMode(agent.id);
  // Version-Skew-Hardening: älterer Master ohne das Feld → beide aus.
  const relay = agent.outboundRelay ?? { final: false, verbose: false };

  function toggle(field: "final" | "verbose", value: boolean) {
    // Merge-PATCH: nur das geänderte Flag senden (Backend merged).
    update.mutate(
      { [field]: value },
      {
        onSuccess: () => toast.success(`Relay ${field}: ${value ? "an" : "aus"}`),
        onError: (err) => toast.error(`Relay ${field} fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  }

  return (
    <SectionCard
      icon={<Megaphone className="h-[18px] w-[18px]" />}
      title="Outbound-Relay"
      description="Was zusätzlich zu mesh__send an den Absender weitergeleitet wird"
    >
      <div className="space-y-3">
        <FlagRow
          label="Finale Antwort weiterleiten (final)"
          hint="Leitet die finale Text-Antwort des Turns weiter — auch wenn das Modell nicht mesh__send aufruft. Modell-agnostische Antwort-Garantie (z.B. für Nicht-Claude-Modelle)."
          checked={relay.final}
          disabled={update.isPending}
          onChange={(v) => toggle("final", v)}
          testId="relay-final"
        />
        <FlagRow
          label="Zwischenschritte weiterleiten (verbose)"
          hint="Zusätzlich kurze Progress-Zeilen pro Tool-Aufruf (nur Tool-Name). Enthält final. Gilt nie für Agent-zu-Agent (A2A) — dort automatisch auf final gedrosselt."
          checked={relay.verbose}
          disabled={update.isPending}
          onChange={(v) => toggle("verbose", v)}
          testId="relay-verbose"
        />
        <p className="text-[11.5px] text-text3">
          Beide aus = heutiges Verhalten (nur explizite <code className="mono">mesh__send</code>-Calls).
          <code className="mono">mesh__send</code> (inkl. Attachments) bleibt immer verfügbar. Wirkt ab der
          nächsten Nachricht. Auch per Slash: <code className="mono">/relay final | verbose | none</code>.
        </p>
      </div>
    </SectionCard>
  );
}

function FlagRow({
  label, hint, checked, disabled, onChange, testId,
}: {
  label: string;
  hint: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
  testId: string;
}) {
  return (
    <label className="flex items-start gap-3">
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4 shrink-0 accent-accent"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
      />
      <span className="min-w-0">
        <span className="block text-[13px] font-semibold text-text">{label}</span>
        <span className="block text-[11.5px] text-text3">{hint}</span>
      </span>
    </label>
  );
}
